import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import errorRedIcon from "Assets/Icons/error-red.svg";
import warningAlertIcon from "Assets/Icons/warning-alert.svg";
import VIPPreferredLogo from "Assets/Img/vipp-logo.png";
import VIPOnlineLogo from "Assets/Img/vipo-logo.png";
import { PRODUCTTYPES, Validators } from "Utils/Constants";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import { Regexes } from "Utils/Regexes";
import { clickifyPhoneNumber } from "Utils/Utils";
import LoadingButton from "Components/LoadingButton";
import FormField from "Components/Fields/FormField";
import { faro } from "@grafana/faro-react";

const OtpValidation = ({ otpSession, verified }) => {
  const [session] = useSessionStorage("vip-patron-session");
  const isVIPPreferred = session?.productType === PRODUCTTYPES.PREFERRED;
  const productType = isVIPPreferred ? "VIP Preferred" : "VIP Online";
  const logoSrc = isVIPPreferred ? VIPPreferredLogo : VIPOnlineLogo;
  const otpRef = useRef(null);
  const vipApi = useVipApiFetcher();
  const [otp, setOtp] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const [newCodeSent, sendNewCode] = useState(null);
  const [validOtpLength, setOtpLength] = useState(false);
  const [resendIsLoading, setResendIsLoading] = useState(null);
  const [untrustedDevice, setDeviceAsUntrusted] = useState(null);
  const [currentOtpSession, updatedOtpSession] = useState(otpSession);
  const [totalFailedOtps, setTotalFailedOtps] = useState({
    otpSent: 1,
    otpSentAttempt: 0,
  });
  const [currentError, setCurrentAttempt] = useState({
    otpError: { attempts: 0, msg: "" },
  });

  const validateOtp = async (event) => {
    event.preventDefault();
    if (!isLoading && validOtpLength) {
      setIsLoading(true);
      try {
        const response = await vipApi.fetch(
          "/api/patronsession/verifyotp",
          "POST",
          {
            oneTimePasscode: otp,
            payloadSessionId: currentOtpSession.payloadSessionId,
          }
        );
        const otpResponse = await response.json();
        switch (response.status) {
          case 200:
            verified(true);
            break;
          case 400:
            setCurrentAttempt((prevState) => {
              return {
                ...prevState,
                otpError: {
                  attempts: Number(currentError.otpError.attempts) + 1,
                  msg: otpResponse.map((item) => item.message).join("\n"),
                },
              };
            });
            setTotalFailedOtps((prevState) => {
              return {
                ...prevState,
                otpSent:
                  newCodeSent && Number(currentError.otpError.attempts) === 5
                    ? Number(totalFailedOtps.otpSent) + 1
                    : Number(totalFailedOtps.otpSent),
                otpSentAttempt:
                  Number(totalFailedOtps.otpSentAttempt) <= 5
                    ? Number(totalFailedOtps.otpSentAttempt) + 1
                    : 1,
              };
            });
            clearForm();
            faro.api.pushError(new Error("Verifying OTP Failure"), { context: otpResponse });
            break;
          default:
            faro.api.pushError(new Error("Verifying OTP Error"), { context: response });
            throw response;
        }
      } catch (err) {
        console.log("Error validating OTP: ", err);
        faro.api.pushError(new Error("Device ID Verify OTP Network Error"), { context: err });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resendCode = async () => {
    sendNewCode(true);
    setResendIsLoading(true);
    clearForm();
    setTotalFailedOtps((prevState) => {
      return {
        ...prevState,
        otpSent:
          Number(totalFailedOtps.otpSent) < 3
            ? Number(totalFailedOtps.otpSent) + 1
            : Number(totalFailedOtps.otpSent),
        otpSentAttempt: 0,
      };
    });
    setCurrentAttempt({ otpError: { attempts: 0, msg: "" } });
    try {
      const response = await vipApi.fetch(
        "/api/patronsession/sendotp",
        "POST",
        {
          sessionid: session.sessionId,
        }
      );
      if (response.status === 200) {
        const otpResponse = await response.json();
        updatedOtpSession((prev) => ({
          ...prev,
          failedAttemptThreshold: otpResponse.failedAttemptThreshold,
          oneTimePasscodePending: otpResponse.oneTimePasscodePending,
          payloadSessionId: otpResponse.payloadSessionId,
        }));
      } else {
        faro.api.pushError(new Error("Device ID Resend OTP Error"), { context: response });
      }
    } catch (err) {
      console.log("Error resending OTP: ", err);
      faro.api.pushError(new Error("Device ID Resend OTP Network Error"), { context: err });
    } finally {
      setResendIsLoading(false);
    }
  };

  const setErrorMsg = () => {
    if (!untrustedDevice && currentError.otpError.attempts === 5) {
      return "The maximum attempts are reached for the verification code. To try again, please resend a new code below.";
    } else if (untrustedDevice) {
      return "You have failed verification, we will close this screen and send you to the cashier's page in 20 seconds.";
    } else if (currentError.otpError.msg) {
      return currentError.otpError.msg;
    }
  };

  const onFieldChange = (fieldName, fieldValue, isValid) => {
    setOtp(fieldValue);
    if (fieldValue.length === 6) {
      setOtpLength(true);
    }
  };

  const clearForm = () => {
    setOtp("");
    setOtpLength(undefined);
    otpRef.current.value ?? otpRef.current.setFieldValue(undefined);
  };

  useEffect(() => {
    if (
      Number(totalFailedOtps.otpSent) === 3 &&
      Number(totalFailedOtps.otpSentAttempt) === 5
    ) {
      verified(false);
      setDeviceAsUntrusted(true);
    }
  }, [totalFailedOtps]);

  let submitButton;
  if (isLoading) {
    submitButton = <LoadingButton />;
  } else {
    submitButton = (
      <Button
        form="otpForm"
        type="submit"
        className={validOtpLength ? "submit-button" : "submit-button disabled"}
      >
        SUBMIT
      </Button>
    );
  }

  let resendButton;
  if (resendIsLoading) {
    resendButton = <LoadingButton />;
  } else {
    resendButton = (
      <button
        className="link submit-button mt-0"
        onClick={resendCode}
        disabled={totalFailedOtps.otpSent === 4}
      >
        RESEND CODE
      </button>
    );
  }

  return (
    <div className="verify-device px-4">
      <div className="header">
        <img className="vip-preferred" src={logoSrc} alt={productType}></img>
      </div>
      <div className="response-message">
        {untrustedDevice === null &&
          (resendIsLoading ? (
            <h5 className="text-center">Sending verification code...</h5>
          ) : (
            <div className="text-center">
              <h5>Enter verification code</h5>
              <p>Sent to +1 XXX-XXX-{session.phoneLast4}</p>
            </div>
          ))}
        <div className="mt-4">
          {currentError.otpError.msg && !isLoading && (
            <Row className="alert alert-danger container mx-auto" role="alert">
              <Col className="alert-message p-0" md={1} xs={1}>
                <img
                  height="20"
                  alt="OTP Entry Error Alert"
                  src={errorRedIcon}
                />
              </Col>
              <Col className="alert-message text-break" md={11} xs={11}>
                <Row>
                  <strong>OTP Entry Error</strong>
                </Row>
                <p>{setErrorMsg()}</p>
              </Col>
            </Row>
          )}
          {!untrustedDevice && (
            <div>
              <Form id="otpForm" onSubmit={validateOtp}>
                <FormField
                  ref={otpRef}
                  value={otp}
                  fieldName="verificationCode"
                  label="VERIFICATION CODE"
                  maxLength={6}
                  required={true}
                  disabled={
                    currentError.otpError.attempts === 5 || resendIsLoading || isLoading
                  }
                  allowedCharacters={Regexes.NUMBER}
                  validators={[Validators.OTP]}
                  onValueChanged={onFieldChange}
                />
                {submitButton}
              </Form>
              <div className="d-grid text-center">{resendButton}</div>
              <Row
                className="alert alert-warning container mx-auto"
                role="alert"
              >
                <Col className="warning-message p-0" md={1} xs={1}>
                  <img
                    height="20"
                    alt="Security Alert"
                    src={warningAlertIcon}
                  />
                </Col>
                <Col className="warning-message" md={11} xs={11}>
                  <Row>
                    <strong>Security Alert</strong>
                  </Row>
                  Pavilion Payment will never call and ask you for this code.
                  Please do not share it.
                </Col>
              </Row>
              <div className="text-center mb-5">
                <span className="small-text">
                  If you don't have access to this phone number, please call
                  Customer Support at{" "}
                </span>
                <div className="phone-number">
                  {clickifyPhoneNumber("1-800-500-1973")}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default OtpValidation;
